import { assign, round } from "lodash";
import { ChangeDetectionStrategy, Component, ElementRef, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import type { MenuItem } from "primeng/api";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-poi',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './poi.component.html',
    styleUrls: ['./poi.component.scss'],
    standalone: false
})
export class PoiComponent {
  @ViewChild('edit_overlay_template_scroll_panel', {static: false}) editPanelScrollPanel: ElementRef<HTMLDivElement>;
  
  poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null;
  #isSubmitted = signal(false);
  poiForm = new FormGroup(
    {
      name: new FormControl(''),
      note: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.PointOfInterestEntityModels.PointOfInterestSchema.pick({
          name: true,
          note: true
        })
      ),
      updateOn: 'change'
    }
  );
  poiAnnotationSynchronizeMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.images_synchronize_exact'),
      command: () => this.#assignAnnotationsToPoiImages(true),
    },
    {
      label: this.translate.instant('global.images_synchronize_only_missing'),
      command: () => this.#assignAnnotationsToPoiImages(false),
    }
  ];
  readonly FlatModels = FlatModels;

  constructor(protected slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              private alertControlFacade: FlatFacades.AlertControlFacade,
              private translate: TranslateService) {
    // Update Poi
    this.slideAuthorFacade.currentSelectedSlidePoi$.pipe(takeUntilDestroyed()).subscribe((poi) => {
      if (this.poi?.id !== poi?.id && this.editPanelScrollPanel) {
        this.editPanelScrollPanel.nativeElement.scrollTop = 0;
      }
      this.poi = structuredClone(poi);
      if (this.poi) {
        this.poiForm.reset(this.poi);
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.poiForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    // Update Poi
    const poi = assign(this.poi, this.poiForm.value);
    this.slideAuthorFacade.requestUpdatePoi(poi, 'history.poi_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.poiForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  getPoiBoundsString(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    if (poi) {
      if (poi.bounds?.x !== null && poi.bounds?.y !== null && poi.bounds?.width !== null && poi.bounds?.height !== null) {
        const { x, y, width, height } = poi.bounds;
        return `(${round(x, 4)}, ${round(y, 4)}) (${round(width, 4)} × ${round(height, 4)})`;
      }
    }
    return 'N/A';
  }
  
  updatePoiPosition(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    this.slideAuthorFacade.requestUpdatePoiPosition(poi).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  goToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    this.slideAuthorFacade.requestGoToPoi(poi).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  #assignAnnotationsToPoiImages(removeOnOtherImages: boolean) {
    if (!this.poi) return;
    this.slideAuthorFacade.requestAssignAnnotationsToPoiImages(this.poi, removeOnOtherImages).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
}
